import * as React from 'react';

import {GreySection} from 'shells/layout/grey-section';
import {PageContentTwoColumn} from 'shells/layout/page-content-two-column';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {FormType, SpamPreventionType} from 'nutshell-graphql-types';
import type {
    CreateRulesFragment,
    FormCreateRulesUpdateInput,
    NextStepsFragment_nutshellNotification,
    NextStepsFragment_slackMessage,
    NextStepsFragment,
} from 'nutshell-graphql-types';

import {AfterSubmissionContainer} from '../after-submission/after-submission-container';
import {CreateRulesContainer} from '../create-rules/create-rules-container';
import {NextStepsContainer} from '../next-steps/next-steps-container';
import {SpamPreventionContainer} from '../spam-prevention/spam-prevention-container';

import styles from './form-page-container.module.css';

type Props = {
    title: string;
    formContent: React.ReactElement<any>;
    formId: string;
    formType?: FormType;
    isConnectedForm?: boolean;
    spamPreventionType?: SpamPreventionType;
    afterSubmissionProps?: {
        afterSubmissionText?: string | null | undefined;
        redirectUrl?: string | null | undefined;
    };
    createRulesProps: {
        hasAccountFields: boolean;
        hasContactFields: boolean;
        hasLeadFields: boolean;
        createRules: CreateRulesFragment;
        updateCreateRules: (arg1: FormCreateRulesUpdateInput) => Promise<any>;
    };
    nextStepsProps: {
        nextSteps: NextStepsFragment;
        description: string;
        bodyText: string;
        slackMessagePreviewMessage: React.ReactNode;
        onUpdateSuccess: (arg1: {
            client: any;
            formId: string;
            nextStepsKey: 'slackMessage' | 'nutshellNotification';
            nextStep:
                | NextStepsFragment_slackMessage
                | NextStepsFragment_nutshellNotification
                | null
                | undefined;
        }) => void;
    };
};

export function FormPageContainer(props: Props) {
    return (
        <PageContentTwoColumn
            withoutOuterPadding={true}
            leftContent={
                <LabelWithContent
                    label={props.title}
                    spacing={8}
                    isFullHeight={true}
                    content={<div className={styles['content-container']}>{props.formContent}</div>}
                />
            }
            rightContent={
                <>
                    {props.spamPreventionType && (
                        <LabelWithContent
                            label='Spam prevention'
                            spacing={8}
                            content={
                                <GreySection
                                    content={
                                        <SpamPreventionContainer
                                            formId={props.formId}
                                            spamPreventionType={props.spamPreventionType}
                                        />
                                    }
                                />
                            }
                        />
                    )}
                    <LabelWithContent
                        label='After Submission'
                        spacing={8}
                        content={
                            <>
                                {props.afterSubmissionProps ? (
                                    <GreySection
                                        content={
                                            // @ts-expect-error
                                            <AfterSubmissionContainer
                                                formId={props.formId}
                                                formType={
                                                    props.formType ? props.formType : undefined
                                                }
                                                {...props.afterSubmissionProps}
                                            />
                                        }
                                    />
                                ) : undefined}
                                <GreySection
                                    content={
                                        <CreateRulesContainer
                                            formId={props.formId}
                                            {...props.createRulesProps}
                                            hasLeadSpecialCases={!props.isConnectedForm}
                                        />
                                    }
                                />
                            </>
                        }
                    />
                    <LabelWithContent
                        label='Notifications'
                        spacing={8}
                        isFullHeight={true}
                        content={
                            <GreySection
                                noMargin={true}
                                isFullHeight={true}
                                content={
                                    <NextStepsContainer
                                        formId={props.formId}
                                        {...props.nextStepsProps}
                                    />
                                }
                            />
                        }
                    />
                </>
            }
        />
    );
}
